/**
 * The polling rule both segment-count endpoints share.
 *
 * `segments/{id}/count` and `relsegments/{id}/count` answer the same
 * `{ count, finished }` shape: membership is computed in the background, so an
 * unfinished response carries a provisional number that is about to change.
 * Both reads therefore poll until `finished`, and both stop at the same limit
 * rather than asking forever about a count that never settles.
 *
 * Expressed as a `refetchInterval` function over the query's own state, which
 * is how this codebase already does data-driven polling (see
 * `getActiveRefetchInterval` in `features/workspace-health`). react-query owns
 * the timer, so there is no hand-rolled loop to cancel on unmount.
 */

import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';

/** A count response paired with whether the backend considers it settled. */
export interface SegmentCountStatus {
  count: number;
  finished: boolean;
}

/** Delay between polls, matching Ember's `APP.shortPollingDelay`. */
export const COUNT_POLL_INTERVAL_MS = 2_000;

/**
 * Attempts spent on a count that never reports `finished` before giving up.
 * Ember polled to the same limit, so a stuck backend stops being asked after
 * roughly two minutes at the interval above rather than polling for the life
 * of the page.
 */
export const MAX_COUNT_POLL_ATTEMPTS = 60;

/**
 * Keep polling while the count is unfinished, up to the attempt limit.
 *
 * Attempts are react-query's own tallies of settled fetches, so the limit is
 * enforced without tracking attempts separately. Both tallies count:
 * `dataUpdateCount` alone only rises on success, so a count endpoint that
 * keeps failing would never reach the limit and would be re-requested every
 * interval for as long as the panel is mounted. `retry` bounds attempts within
 * one fetch, not this loop, and a 4xx is not retried at all.
 */
export function pollWhileUnfinished(query: {
  state: { data: SegmentCountStatus | undefined; dataUpdateCount: number; errorUpdateCount: number };
}): number | false {
  const { data, dataUpdateCount, errorUpdateCount } = query.state;
  if (data?.finished === true) return false;
  if (dataUpdateCount + errorUpdateCount > MAX_COUNT_POLL_ATTEMPTS) return false;
  return COUNT_POLL_INTERVAL_MS;
}

/**
 * The read both count endpoints share: poll one segment's count until it
 * settles, without suspending and without firing until there is a segment to
 * ask about.
 *
 * Both endpoints answer the same shape under the same polling rule, so only
 * the key and the fetcher differ. Callers pass those; everything else — the
 * workspace gate, the inactive sentinel, the interval — lives here so the two
 * hooks cannot drift apart.
 *
 * `enabled` and a null `segmentId` both mean "not yet": the query registers no
 * request at all rather than asking about a segment that does not exist.
 */
export function useSegmentCountPolling(input: {
  segmentId: string | null;
  enabled: boolean;
  /** Sentinel key used while inactive; keeps the empty slot out of the workspace namespace. */
  inactiveKey: readonly unknown[];
  countKey: (workspaceId: number, segmentId: string) => readonly unknown[];
  fetchCount: (workspaceId: number, segmentId: string) => Promise<SegmentCountStatus>;
}) {
  const { segmentId, enabled, inactiveKey, countKey, fetchCount } = input;
  const urlWorkspaceId = useWorkspaceIdFromCurrentUrl();
  const workspaceId = enabled && urlWorkspaceId != null && urlWorkspaceId > 0 ? urlWorkspaceId : null;
  const active = workspaceId != null && segmentId != null;

  const queryKey = active ? countKey(workspaceId, segmentId) : inactiveKey;
  const query = useQuery({
    queryKey,
    queryFn: active ? () => fetchCount(workspaceId, segmentId) : skipToken,
    // A live number; never serve it from cache alone.
    staleTime: 0,
    refetchInterval: pollWhileUnfinished,
  });

  // `pollWhileUnfinished` stops at the limit but the query keeps its last
  // unfinished response, which by result fields alone is indistinguishable
  // from "still working". The attempt tallies live on the query state, not the
  // result, so read them from the cache to tell the two apart.
  const state = useQueryClient().getQueryState<SegmentCountStatus>(queryKey);
  const attempts = (state?.dataUpdateCount ?? 0) + (state?.errorUpdateCount ?? 0);
  const isPollExhausted = query.data?.finished !== true && attempts > MAX_COUNT_POLL_ATTEMPTS;

  return { ...query, isPollExhausted };
}
